package org.teddapp.project.sync

import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import org.teddapp.project.core.CoreJournalTransport
import org.teddapp.project.core.TeddCoreBridge
import org.teddapp.project.server.ServerSession
import org.teddapp.project.server.ServerStore

/** Sous quelle clé on retient vers quel serveur et quel compte le journal du noyau est déjà parti. */
internal const val KEY_CORE_ORIGIN = "server.core_origin"

/**
 * Transport du journal du noyau vers le serveur teddApp (`/v1/events`). C'est par lui
 * que les heures, les paliers et les feuilles cochées d'un appareil arrivent sur les
 * autres : chaque noyau ne calcule que sur les faits de son journal.
 *
 * Le serveur ignore les faits qu'il connaît déjà (idempotence sur `event_id`) et
 * attribue l'offset global ; le noyau garde le curseur et l'ordre de rejeu.
 *
 * Premier cycle vers un serveur ou un compte ([origin]) : le curseur du noyau vient
 * peut-être d'un autre broker (Supabase), il croirait déjà publiés des faits que ce
 * serveur n'a jamais reçus. On publie donc tout le journal local, puis on relit le
 * journal du serveur depuis le début. Les doublons sont écartés des deux côtés.
 */
class ServerJournalTransport(
    private val session: ServerSession,
    private val store: ServerStore,
    private val origin: String,
) : CoreJournalTransport {

    /** Vrai tant que ce serveur n'a pas reçu le journal entier ; lu une fois dans les préférences. */
    private var fresh: Boolean? = null

    private suspend fun isFresh(): Boolean =
        fresh ?: (store.get(KEY_CORE_ORIGIN) != origin).also { fresh = it }

    override suspend fun prepare(core: TeddCoreBridge) {
        if (isFresh()) publish(core.eventsAfter(0))
    }

    override suspend fun publish(envelopes: List<String>): List<String> =
        journalBatches(envelopes).flatMap { batch ->
            session.authed { api, access -> api.publishEvents(access, batch) }
        }

    override suspend fun fetchAfter(after: Long): List<String> {
        var from = if (isFresh()) 0L else after
        val all = mutableListOf<String>()
        while (true) {
            val page = session.authed { api, access -> api.fetchEvents(access, from, PAGE) }
            all += page
            if (page.size < PAGE) return all
            from = globalOffset(page.last()) ?: return all
        }
    }

    override suspend fun afterCycle() {
        if (fresh != true) return
        store.set(KEY_CORE_ORIGIN, origin)
        fresh = false
    }

    private fun globalOffset(record: String): Long? =
        runCatching { Json.parseToJsonElement(record).jsonObject["global_offset"]?.jsonPrimitive?.longOrNull }.getOrNull()

    private companion object {
        /** Taille de page demandée au serveur, qui en accepte 1 000 au plus. */
        const val PAGE = 500
    }
}

/** Faits par requête : le serveur en refuse plus de 500. */
internal const val JOURNAL_MAX_EVENTS = 500

/** Octets par requête : le serveur refuse les corps de plus de 2 Mo, on garde de la marge. */
internal const val JOURNAL_MAX_BYTES = 1_500_000

/**
 * Découpe des faits en lots que le serveur accepte : au plus [maxEvents] faits et
 * [maxBytes] octets. Un fait plus gros que [maxBytes] part seul.
 */
internal fun journalBatches(
    envelopes: List<String>,
    maxEvents: Int = JOURNAL_MAX_EVENTS,
    maxBytes: Int = JOURNAL_MAX_BYTES,
): List<List<String>> {
    val batches = mutableListOf<List<String>>()
    var current = mutableListOf<String>()
    var bytes = 0
    for (envelope in envelopes) {
        val size = envelope.encodeToByteArray().size + 1
        if (current.isNotEmpty() && (current.size >= maxEvents || bytes + size > maxBytes)) {
            batches += current
            current = mutableListOf()
            bytes = 0
        }
        current += envelope
        bytes += size
    }
    if (current.isNotEmpty()) batches += current
    return batches
}
